import { Lightning, WebhooksLogo, X, Question } from '@phosphor-icons/react';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { RadioGroup } from 'src/components/RadioGroup';
import { SelectBox } from 'src/components/SelectBox';
import { Tooltip } from 'src/components/Tooltip';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import {
  Agent,
  BackgroundSound,
  Language,
  LLMProvider,
} from 'src/interfaces/agent.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Voice } from 'src/interfaces/voice.interface';
import { Webhook, WebhookPayload } from 'src/interfaces/webhook.interface';
import { AgentActionsModal } from 'src/pages/Actions/components/AgentActionsModal';
import { voices } from 'src/pages/Voices/ListVoices';
import { WebhookForm } from 'src/pages/Webhooks/components/WebhookForm';
import { limitString } from 'src/utils/limitString';
import { useAgentActions } from '../contexts/AgentActionsContext';
import { useTransientSelectedActions } from '../contexts/TransientSelectedActionsContext';
import { BetaTag } from 'src/components/BetaTag';
import { useNotification } from 'src/contexts/NotificationContext';

export const languages: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'de', label: 'German' },
  { value: 'hi', label: 'Hindi' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'fr', label: 'French' },
  { value: 'nl', label: 'Dutch' },
  { value: 'id', label: 'Indonesian' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
];

const llmProviders = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'google', label: 'Google' },
  { value: 'azure', label: 'Azure OpenAI' },
  { value: 'deepseek', label: 'Deepseek', disabled: true },
  { value: 'groq', label: 'Groq' },
  { value: 'togetherai', label: 'Together AI', disabled: true },
];

const providerModels: Record<
  string,
  { value: string; label: string; disabled?: boolean }[]
> = {
  openai: [
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
    { value: 'gpt-3.5', label: 'GPT-3.5' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  ],
  google: [
    { value: 'gemini-3-flash-preview', label: 'Gemini 3.0 Flash' },
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', disabled: true },
    {
      value: 'gemini-2.0-flash-lite',
      label: 'Gemini 2.0 Flash Lite',
      disabled: true,
    },
    {
      value: 'gemini-2.0-flash-exp',
      label: 'Gemini 2.0 Flash Exp',
      disabled: true,
    },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', disabled: true },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', disabled: true },
  ],
  azure: [{ value: 'gpt-4o-mini', label: 'GPT-4o Mini' }],
  deepseek: [{ value: 'deepseek-chat', label: 'Deepseek Chat' }],
  groq: [
    { value: 'llama-3.3-70b-versatile', label: 'Llama 3.3 70B' },
    { value: 'llama-3.1-8b-instant', label: 'Llama 3.1 8B' },
    { value: 'meta-llama/llama-guard-4-12b', label: 'Llama Guard 4 12B' },
    { value: 'openai/gpt-oss-20b', label: 'GPT-OSS 20B' },
    { value: 'openai/gpt-oss-120b', label: 'GPT-OSS 120B' },
    { value: 'groq/compound', label: 'Compound' },
    { value: 'groq/compound-mini', label: 'Compound Mini' },
    { value: 'qwen/qwen3-32b', label: 'Qwen 3.3 32B', disabled: true },
  ],
  togetherai: [
    {
      value: 'meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo',
      label: 'Llama 3.1 Instruct Turbo',
    },
    {
      value: 'arcee-ai/maestro-reasoning',
      label: 'Arcee AI LLM',
    },
    {
      value: 'Gryphe/MythoMax-L2-13b',
      label: 'Gryphe LLM',
    },
    {
      value: 'Gryphe/MythoMax-L2-13b-Lite',
      label: 'Gryphe LLM (Lite)',
    },
    {
      value: 'meta-llama/Llama-3.3-70B-Instruct-Turbo',
      label: 'Meta Llama 3.3',
    },
    {
      value: 'meta-llama/Llama-3.3-70B-Instruct-Turbo-Free',
      label: 'Free version',
    },
    {
      value: 'meta-llama/Meta-Llama-3-70B-Instruct-Turbo',
      label: 'Meta Llama 3',
    },
    {
      value: 'mistralai/Mistral-7B-Instruct-v0.2',
      label: 'Mistral LLM',
    },
    {
      value: 'mistralai/Mistral-Small-24B-Instruct-2501',
      label: 'Mistral Small 24B',
    },
    {
      value: 'Qwen/Qwen2.5-7B-Instruct-Turbo',
      label: 'Qwen 7B',
    },
    {
      value: 'Qwen/Qwen2-VL-72B-Instruct',
      label: 'Multimodal LLM',
    },
    {
      value: 'Qwen/Qwen3-235B-A22B-fp8-tput',
      label: 'Large model',
    },
    {
      value: 'Qwen/QwQ-32B',
      label: '32B model',
    },
    {
      value: 'scb10x/scb10x-llama3-1-typhoon2-8b-instruct',
      label: 'SCB10x LLaMA3 variant',
    },
    {
      value: 'togethercomputer/MoA-1',
      label: 'MoA-1',
    },
    {
      value: 'google/gemma-2-27b-it',
      label: 'Gemma 27B',
    },
    {
      value: 'google/gemma-2-9b-it',
      label: 'Gemma 9B',
    },
    {
      value: 'google/gemma-2b-it',
      label: 'Gemma 2B',
    },
    {
      value: 'deepseek-ai/DeepSeek-R1-Distill-Llama-70B-free',
      label: 'DeepSeek LLaMA variant',
    },
  ],
};

const defaultTranscriberOption = { value: 'assemblyai', label: 'AssemblyAI' };

const transcribersOptions = [
  defaultTranscriberOption,
  { value: 'deepgram', label: 'Deepgram' },
  { value: 'gladia', label: 'Gladia' },
];

const backgroundSoundOptions = Object.values(BackgroundSound).map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

type AdvancedSettingsProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
  defaultPrompt: Prompt | undefined;
  finishAction: (() => Promise<void>) | undefined;
};

export const AdvancedSettings = ({
  agent,
  setAgent,
  setLoading,
  loading = false,
}: AdvancedSettingsProps) => {
  const { get } = useAuthedRequest();
  const notification = useNotification();

  const { globalActions, selectedActions, resetActions } = useAgentActions();

  const { setSelectedActions: setSelectedTransientActions } =
    useTransientSelectedActions();

  const [webhookModal, setWebhookModal] = useState(false);
  const [actionsModal, setActionsModal] = useState(false);

  const [currentWebhook, setCurrentWebhook] = useState<Webhook | undefined>();
  const [nextWebhook, setNextWebhook] = useState<Webhook | undefined>({
    label: '',
    url: '',
    method: 'POST',
    subscriptions: [],
    account_connection_id: '',
  } as unknown as Webhook);

  const [currentVoice, setCurrentVoice] = useState<Voice | undefined>();
  const [originalMemoryScope] = useState<
    'all' | 'single_agent' | null | undefined
  >(agent.memory_scope);

  const searchVoices = useCallback(async (label: string) => {
    const queryObject: QueryObject = {
      filters: JSON.stringify({
        scope: 'global|user',
        label: label.length !== 0 ? label : undefined,
      }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/voices?${query.toString()}`);

    return data.items.map((voice: Voice) => ({
      value: voice.id,
      label: voice.label + ` (${findProvider(voice.type)})`,
    }));
  }, []);

  const findProvider = (type: string) => {
    return voices.find((voice) => voice.value === type)?.label;
  };

  const searchWebhooks = useCallback(async (url: string) => {
    const queryObject: QueryObject = {
      filters: JSON.stringify({
        scope: 'global|user',
        url: url.length !== 0 ? url : undefined,
      }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/webhooks?${query.toString()}`);

    return data.items.map((webhook: Webhook) => ({
      value: webhook.id,
      label: webhook.label || webhook.url,
    }));
  }, []);

  const fetchWebhook = async (id: string): Promise<Webhook> => {
    if (!id) {
      return {} as Webhook;
    }

    return get(`/webhooks/${id}`);
  };

  const handleSaveWebhook = async () => {
    setLoading(true);

    if (agent.webhook) {
      const fetchedWebhook: Webhook = nextWebhook?.id
        ? await fetchWebhook(nextWebhook?.id)
        : ({} as Webhook);

      const diff = _.isEqual(
        {
          url: fetchedWebhook.url,
          label: fetchedWebhook.label,
          description: fetchedWebhook.description,
          subscriptions: _.sortBy(fetchedWebhook.subscriptions || []),
          account_connection_id: fetchedWebhook.account_connection_id,
        },
        {
          url: nextWebhook?.url,
          label: nextWebhook?.label,
          description: nextWebhook?.description,
          subscriptions: _.sortBy(nextWebhook?.subscriptions || []),
          account_connection_id: nextWebhook?.account_connection_id,
        },
      )
        ? {}
        : _.omitBy(nextWebhook, (value, key) => {
            if (key === 'subscriptions') {
              return _.isEqual(
                _.sortBy(fetchedWebhook.subscriptions || []),
                _.sortBy(value || []),
              );
            }
            return fetchedWebhook[key as keyof Webhook] === value;
          });

      if (Object.keys(diff).length > 0) {
        const updatedWebhook = {
          ...fetchedWebhook,
          ...diff,
        };

        setAgent({ ...agent, webhook: updatedWebhook });
        setCurrentWebhook(updatedWebhook);
        setNextWebhook(updatedWebhook);
      } else {
        setAgent({ ...agent, webhook: fetchedWebhook });
        setCurrentWebhook(fetchedWebhook);
        setNextWebhook(fetchedWebhook);
      }
    } else {
      if (
        Object.entries(nextWebhook || {}).some(
          ([key, value]) =>
            key !== 'method' && value !== '' && value !== undefined,
        ) &&
        nextWebhook
      ) {
        setCurrentWebhook(nextWebhook);
        setAgent({ ...agent, webhook: nextWebhook });
      }
    }

    setWebhookModal(false);
    setLoading(false);
  };

  useEffect(() => {
    const updateWebhook = async () => {
      if (typeof agent.webhook === 'string') {
        setLoading?.(true);
        const webhook = await fetchWebhook(agent.webhook as string);
        setCurrentWebhook(webhook);
        setNextWebhook(webhook);
        setLoading?.(false);
      }
    };

    updateWebhook();
  }, [agent.webhook]);

  useEffect(() => {
    if (agent.voice) {
      const fetchVoice = async () => {
        if (typeof agent.voice !== 'string' && !agent.voice?.id) {
          setCurrentVoice(agent.voice);
          return;
        }

        const voice = await get(
          `/voices/${typeof agent.voice === 'string' ? agent.voice : agent.voice?.id}`,
        );
        setCurrentVoice(voice);
      };

      fetchVoice();
    }
  }, [agent.voice]);

  const isValidWebhook = () => {
    return !!agent.webhook && JSON.stringify(agent.webhook) != '{}';
  };

  const openActionsModal = () => {
    setActionsModal(true);
    setSelectedTransientActions(selectedActions);
  };

  const languageOptions =
    agent.transcriber?.type === 'deepgram'
      ? [...languages, { value: 'multi', label: 'Multilingual' }]
      : languages;

  return (
    <div className="flex flex-col md:grid md:grid-cols-2 gap-2">
      <div className="mb-3">
        <Label required htmlFor="voice">
          Voice
        </Label>

        <AutoCompleteSelectBox
          key={
            (currentVoice?.id || String(currentVoice?.voice_id)) +
            '_' +
            currentVoice?.label
          }
          defaultValue={
            currentVoice?.id || currentVoice?.voice_id
              ? {
                  value: currentVoice.id || String(currentVoice.voice_id),
                  label: currentVoice.label,
                }
              : undefined
          }
          fetchOptions={searchVoices}
          onChange={(value) => {
            setAgent({ ...agent, voice: value });
          }}
          className="min-w-[140px]"
          variant="outlined"
          color="primary"
          size="medium"
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>

      <div className="mb-3">
        <Label htmlFor="language">Language</Label>

        <SelectBox
          options={languageOptions}
          key={agent.language}
          defaultValue={{
            value: agent.language,
            label: languageOptions.find((lang) => lang.value === agent.language)
              ?.label,
          }}
          variant="outlined"
          color="primary"
          size="medium"
          onChange={(value) =>
            setAgent({ ...agent, language: value as Language })
          }
          className="w-full"
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>
      <div className="mb-3">
        <Label htmlFor="provider">LLM Provider</Label>

        <SelectBox
          options={llmProviders.filter((provider) => !provider.disabled)}
          key={agent.provider}
          defaultValue={{
            value: agent.provider || 'openai',
            label: llmProviders.find(
              (provider) => provider.value === (agent.provider || 'openai'),
            )?.label,
          }}
          variant="outlined"
          color="primary"
          size="medium"
          onChange={(value) =>
            setAgent({
              ...agent,
              provider: value as LLMProvider,
              model: undefined,
            })
          }
          className="min-w-[140px]"
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>
      <div className="mb-3">
        <Label htmlFor="model">Model Name</Label>
        <SelectBox
          options={providerModels[agent.provider || 'openai'].filter(
            (model) => !model.disabled,
          )}
          key={agent.model}
          defaultValue={
            agent.model
              ? {
                  value: String(agent.model),
                  label: providerModels[agent.provider || 'openai'].find(
                    (model) => model.value === agent.model,
                  )?.label,
                }
              : undefined
          }
          variant="outlined"
          color="primary"
          size="medium"
          onChange={(value) =>
            setAgent({
              ...agent,
              model: value as string,
            })
          }
          className="min-w-[140px]"
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>
      <div className="mb-3">
        <Label htmlFor="action">Speech to Text</Label>

        <AutoCompleteSelectBox
          key="agent-transcriber"
          defaultValue={
            agent.transcriber
              ? transcribersOptions.find(
                  ({ value }) => value === agent?.transcriber?.type,
                )
              : defaultTranscriberOption
          }
          defaultOptions={transcribersOptions}
          variant="outlined"
          color="primary"
          size="medium"
          className="min-w-[140px]"
          onChange={(value) =>
            setAgent({
              ...agent,
              transcriber: { type: value },
              language:
                value !== 'deepgram' && agent.language === 'multi'
                  ? languages[0].value
                  : agent.language,
            })
          }
        />
      </div>
      <div className="mb-3">
        <Label htmlFor="action">Background Sound</Label>

        <AutoCompleteSelectBox
          key="agent-background-sound"
          defaultValue={
            agent.background_sound
              ? backgroundSoundOptions.find(
                  (option) => option.value === agent.background_sound,
                )
              : undefined
          }
          defaultOptions={backgroundSoundOptions}
          variant="outlined"
          color="primary"
          size="medium"
          className="min-w-[140px]"
          onChange={(value) =>
            setAgent({
              ...agent,
              background_sound: value as BackgroundSound,
            })
          }
        />
      </div>
      <div className="mb-3">
        <div
          className="mt-2 p-3 bg-neutral-0 rounded-lg border border-neutral-100 cursor-pointer w-full"
          style={{ boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)' }}
          onClick={() => setWebhookModal(true)}
        >
          {isValidWebhook() && (
            <div className="flex justify-between items-center">
              <div className="flex gap-3">
                <WebhooksLogo className="w-6 h-6 mt-2" />

                <div className="">
                  <p className="font-medium">Webhook</p>
                  <p className="font-normal truncate">
                    {currentWebhook?.label
                      ? limitString(currentWebhook?.label, 30)
                      : 'No label'}
                  </p>
                </div>
              </div>

              <Tooltip content="Remove webhook">
                <X
                  className="w-5 h-5 text-neutral-400 hover:text-neutral-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentWebhook(undefined);
                    setAgent({ ...agent, webhook: '' });
                  }}
                />
              </Tooltip>
            </div>
          )}

          {!isValidWebhook() && (
            <div className="flex justify-between items-center">
              <div className="flex gap-3 min-h-12">
                <WebhooksLogo className="w-6 h-6 mt-3" />

                <div className="justify-center mt-3">
                  <p className="font-medium">Add Webhook</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mb-3">
        <div
          className="mt-2 p-3 bg-neutral-0 rounded-lg border border-neutral-100 cursor-pointer w-full"
          style={{ boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)' }}
          onClick={() => openActionsModal()}
        >
          {selectedActions.length > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex gap-3">
                <Lightning className="w-6 h-6 mt-2" />

                <div className="">
                  <p className="font-medium">Actions</p>
                  <p className="font-normal">
                    {limitString(
                      globalActions
                        .filter((action) => selectedActions.includes(action.id))
                        .map((action) => action.label)
                        .join(', '),
                      30,
                    ) || 'No actions'}
                  </p>
                </div>
              </div>

              <Tooltip content="Remove all actions">
                <X
                  className="w-5 h-5 text-neutral-400 hover:text-neutral-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetActions();
                    setAgent({ ...agent, actions: [] });
                  }}
                />
              </Tooltip>
            </div>
          )}

          {selectedActions.length === 0 && (
            <div className="flex justify-between items-center">
              <div className="flex gap-3 min-h-12">
                <Lightning className="w-6 h-6 mt-3" />

                <div className="justify-center mt-3">
                  <p className="font-medium">Add Actions</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mb-3">
        <Label htmlFor="llmTemperature">LLM Temperature</Label>

        <input
          type="range"
          id="llmTemperature"
          min="0"
          max="1"
          step="0.1"
          value={agent.llm_temperature}
          onChange={(e) =>
            setAgent({
              ...agent,
              llm_temperature: parseFloat(e.target.value),
            })
          }
          className="w-full h-2 bg-primary rounded-lg appearance-none cursor-pointer range-thumb"
        />

        <span className="text-neutral-600/70 text-sm font-bold">
          {agent.llm_temperature}
        </span>
      </div>
      <div className="mb-3">
        <div className="flex justify-between">
          <Label htmlFor="conversationSpeed">Conversation Speed</Label>
          <div className="flex gap-2">
            <Label htmlFor="enableDynamicTurns">Auto</Label>
            <Checkbox
              checked={agent.enable_dynamic_turns || false}
              onChange={(newChecked) =>
                setAgent({
                  ...agent,
                  enable_dynamic_turns: newChecked,
                })
              }
              ariaLabel="Enable Auto Conversation Speed"
            />
          </div>
        </div>

        <input
          type="range"
          id="conversationSpeed"
          min="0.2"
          max="5"
          step="0.2"
          value={agent.conversation_speed}
          onChange={(e) =>
            setAgent({
              ...agent,
              conversation_speed: parseFloat(e.target.value),
            })
          }
          disabled={agent.enable_dynamic_turns || false}
          className={`w-full h-2 rounded-lg appearance-none range-thumb ${
            agent.enable_dynamic_turns
              ? 'bg-neutral-200 cursor-not-allowed opacity-60'
              : 'bg-primary cursor-pointer'
          }`}
        />

        <span className="text-neutral-600/70 text-sm font-bold">
          {agent.conversation_speed}
        </span>
      </div>
      <div className="mb-3">
        <Label htmlFor="maxIdleCheckCount">Max Idle Check Count</Label>

        <Input
          type="number"
          id="maxIdleCheckCount"
          min="0"
          value={agent.max_idle_check_count || ''}
          onChange={(e) =>
            setAgent({
              ...agent,
              max_idle_check_count: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            })
          }
          className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder="Enter max idle check count"
        />
      </div>
      <div className="mb-3">
        <Label htmlFor="idleTimeSeconds">Idle Time (seconds)</Label>

        <Input
          type="number"
          id="idleTimeSeconds"
          min="0"
          value={agent.idle_time_seconds || ''}
          onChange={(e) =>
            setAgent({
              ...agent,
              idle_time_seconds: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            })
          }
          className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder="Enter idle time in seconds"
        />
      </div>
      <div className="mb-1">
        <div className="flex justify-between">
          <Label htmlFor="callDurationSec">Call Duration (seconds)</Label>
          <div className="flex gap-1">
            <Label htmlFor="enableCallExtension">Extend Call</Label>
            <Tooltip
              content="Automatically reconnect when duration limit is reached"
              position="right"
            >
              <Question size={16} />
            </Tooltip>
            <Checkbox
              checked={agent.transfer_on_end || false}
              onChange={(newChecked) =>
                setAgent({
                  ...agent,
                  transfer_on_end: newChecked,
                })
              }
              ariaLabel="Enable Auto-Extend Call"
            />
          </div>
        </div>

        <Input
          type="number"
          id="callDurationSec"
          min="0"
          max="3600"
          value={agent.call_duration_sec || ''}
          onChange={(e) => {
            let callDurationSec = e.target.value;

            if (parseInt(e.target.value) > 3600) {
              callDurationSec = '3600';

              notification.warning(
                'Call duration cannot be greater than 1 hour (3600 seconds)',
              );
            }

            setAgent({
              ...agent,
              call_duration_sec: callDurationSec
                ? parseInt(callDurationSec)
                : undefined,
            });
          }}
          className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder="Enter call duration in seconds"
        />
      </div>
      <div className="flex-col col-start-1 mb-3">
        <div className="flex justify-between">
          <Label htmlFor="enableRecording" className="mt-2">
            Enable Recording
          </Label>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={agent.enable_recording || false}
              onChange={(newChecked) =>
                setAgent({
                  ...agent,
                  enable_recording: newChecked,
                })
              }
              disabled={false}
              ariaLabel="Enable Recording"
            />
          </div>
        </div>
        <div className="flex justify-between">
          <div className="flex items-center gap-2">
            <Label htmlFor="reuseLastCallContext" className="mt-2">
              Outbound Context Memory
            </Label>
            <Tooltip
              content="Inbound calls combine context with the last outbound call (same numbers), filling only missing details."
              position="right"
            >
              <Question size={16} className="-translate-y-[1px]" />
            </Tooltip>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={agent.combine_last_call_context || false}
              onChange={(newChecked) =>
                setAgent({
                  ...agent,
                  combine_last_call_context: newChecked,
                })
              }
              disabled={false}
              ariaLabel="Outbound Context Memory"
            />
          </div>
        </div>
        {originalMemoryScope && (
          <div className="flex justify-between">
            <div className="flex mt-2 gap-2">
              <Label htmlFor="enableMemory">Conversation Memory</Label>
              <BetaTag />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={!!agent.memory_scope || false}
                onChange={(newChecked) =>
                  setAgent({
                    ...agent,
                    memory_scope: newChecked ? 'single_agent' : null,
                  })
                }
                disabled={false}
                ariaLabel="Conversation Memory"
              />
            </div>
          </div>
        )}
        {agent.memory_scope && (
          <RadioGroup
            items={[
              { value: 'single_agent', content: <p>This Agent</p> },
              {
                value: 'all',
                content: <p>Shared Across Agents (Same Phone Pair)</p>,
              },
            ]}
            defaultValue={agent.memory_scope}
            onChange={(value) =>
              setAgent({
                ...agent,
                memory_scope: value as 'all' | 'single_agent',
              })
            }
          />
        )}
        <div className="flex justify-between">
          <div className="flex mt-2 gap-2">
            <Label htmlFor="dynamicVmDetection">
              Dynamic Voicemail Detection
            </Label>
            <BetaTag />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={agent.enable_dynamic_vm_detection || false}
              onChange={(newChecked) =>
                setAgent({
                  ...agent,
                  enable_dynamic_vm_detection: newChecked,
                })
              }
              ariaLabel="Dynamic Voicemail Detection"
            />
          </div>
        </div>
      </div>

      <Modal
        title={agent.webhook ? `Edit Webhook` : `Add Webhook`}
        isOpen={webhookModal}
        onClose={() => setWebhookModal(false)}
        actionButton={
          <Button
            onClick={handleSaveWebhook}
            disabled={
              loading ||
              !nextWebhook?.subscriptions?.length ||
              !nextWebhook?.url
            }
          >
            Save
          </Button>
        }
      >
        <div className="max-w-md">
          <Label htmlFor="prompt">Webhook</Label>

          <AutoCompleteSelectBox
            key={nextWebhook?.id}
            defaultValue={
              nextWebhook?.id || nextWebhook?.label
                ? {
                    value: nextWebhook.id,
                    label: nextWebhook.label || nextWebhook.url,
                  }
                : undefined
            }
            fetchOptions={searchWebhooks}
            onChange={async (value) => {
              setLoading(true);
              const webhook = await fetchWebhook(value as string);
              setNextWebhook(webhook);
              setLoading(false);
            }}
            className="min-w-[140px] bg-neutral-0"
            variant="outlined"
            color="primary"
            size="medium"
            disabled={loading || (!agent.user_id && !!agent.id)}
          />
        </div>

        <div className="flex justify-end italic text-sm mt-2">
          * Required fields
        </div>

        <div className="bg-neutral-50/65 rounded-md p-4">
          <WebhookForm
            key={nextWebhook?.id}
            form={nextWebhook as WebhookPayload}
            onChange={(form) =>
              setNextWebhook(
                (prev) =>
                  ({
                    ...prev,
                    ...form,
                  }) as unknown as Webhook,
              )
            }
            loading={loading}
            selectedId={
              (agent.webhook as Webhook)?.id || (agent.webhook as string)
            }
            showEquivalentCode={false}
            showRequiredFields={false}
          >
            {nextWebhook?.id && (
              <div className="inline-block gap-2.5 p-2.5 text-sm bg-primary-100 rounded-md mb-6">
                Editing this setting will create it as a new setting
              </div>
            )}

            {!nextWebhook?.id && (
              <div className="inline-block gap-2.5 p-2.5 text-sm bg-primary-100 rounded-md mb-6">
                Fill in the webhook details to create a new webhook
              </div>
            )}
          </WebhookForm>
        </div>
      </Modal>

      <AgentActionsModal
        isOpen={actionsModal}
        onClose={() => setActionsModal(false)}
        onOpen={() => setActionsModal(true)}
      />
    </div>
  );
};
